import { createContentfulComponent } from "frontend/contentful/lib/create-contentful-component";
import { EventScheduleEntry } from "frontend/contentful/schema/blocks";
import { EventAgendaItemEntry } from "frontend/contentful/schema/primitives";
import { useSearch } from "frontend/hooks/use-search";
import { ModernSearchHit, SearchDisplay } from "frontend/types";
import { EventSchedule } from "design-system/components/blocks/event-schedule/event-schedule";
import { EventScheduleItemProps } from "design-system/components/blocks/event-schedule/event-schedule-item";
import { EventAgendaItemProps } from "design-system/components/blocks/event-schedule/event-agenda-item";
import { useContentful } from "frontend/hooks/use-contentful";
import {
  ContributorEntry,
  LocationEntry,
  PersonWrapperEntry,
  TermEntry,
} from "frontend/contentful/schema/semantics";
import { RenderContentfulRichText } from "frontend/contentful/components/utils/render-contentful-rich-text";
import { format } from "date-fns";
import { isContentType } from "frontend/contentful/lib/is-content-type";
import { MediaAssetProps } from "design-system/components/primitives/media-asset/media-asset";
import { createMediaAssetProps } from "../primitives/media-asset.props";

interface EventMetaItem {
  title: string;
  info: string;
}

type EventMetaGroup = Array<EventMetaItem>;
type EventMeta = Array<EventMetaGroup>;

interface EventSearchDisplay extends SearchDisplay {
  eventMeta?: EventMeta;
}

function formatTimeTo12Hour(time: string) {
  const [hour, minute] = time.split(":");
  const date = new Date();
  date.setHours(Number(hour), Number(minute));
  return format(date, "h:mm a");
}

export const ContentfulEventSchedule =
  createContentfulComponent<EventScheduleEntry>(({ entry }) => {
    const { getEntries, getEntry, getPersonLink, getEntityName } =
      useContentful();
    const ids = entry.fields.events || [];
    const agendaItems = entry.fields.agendaItems || [];

    const { data } = useSearch<ModernSearchHit>(
      {
        index: "modern",
        query: ids.map((id) => `id:${id}`).join(" OR "),
        size: entry.fields.events?.length || 25,
      },
      {
        enabled: ids.length > 0 ? true : false,
      },
    );

    if (agendaItems.length > 0) {
      const eventsByDate = new Map<string, Array<EventAgendaItemProps>>();

      const agendaItemsEntries = getEntries<EventAgendaItemEntry>(agendaItems);
      agendaItemsEntries.forEach((item) => {
        if (!item.fields.startTime || !item.fields.title) {
          return null;
        }

        const date = item.fields.startTime.split("T")[0]!;
        const startTimeRaw = item.fields.startTime.split("T")[1]?.slice(0, 5);
        const endTimeRaw = item.fields.endTime
          ? item.fields.endTime.split("T")[1]?.slice(0, 5)
          : undefined;
        const startTime = formatTimeTo12Hour(startTimeRaw!);
        const endTime = endTimeRaw ? formatTimeTo12Hour(endTimeRaw) : undefined;

        const contributorRefs = item.fields.contributors ?? [];
        const contributors = getEntries<ContributorEntry>(contributorRefs)
          .map((contributorEntry) => {
            const personEntry = getEntry(contributorEntry.fields.person);
            const resolvedPerson = isContentType<PersonWrapperEntry>(
              "personWrapper",
              personEntry,
            )
              ? getEntry(personEntry.fields.person)
              : personEntry;

            const name = resolvedPerson ? getEntityName(personEntry) : "";

            const contributorType: Array<string> = [];
            const termEntries = getEntries<TermEntry>(
              contributorEntry.fields.contributorType || [],
            );
            termEntries.forEach((termEntry) => {
              const title = termEntry?.fields?.title;
              if (title) contributorType.push(title);
            });

            const link = getPersonLink(contributorEntry.fields.person);

            let avatar: MediaAssetProps | undefined;
            if (
              resolvedPerson?.fields &&
              "image" in resolvedPerson.fields &&
              resolvedPerson.fields.image
            ) {
              const mediaAsset = createMediaAssetProps(
                resolvedPerson.fields.image,
              );
              if (mediaAsset?.image) {
                avatar = { image: mediaAsset.image };
              }
            }
            return { name, link, avatar, contributorType };
          })
          .filter((c) => Boolean(c.name));

        const speakers = contributors.length ? contributors : undefined;

        const location = getEntry<LocationEntry>(item.fields.location);

        const agendaItem: EventAgendaItemProps = {
          title: item.fields.title,
          times: endTime ? [startTime!, endTime] : [startTime!],
          location: location
            ? { label: location.fields.title || "" }
            : undefined,
          subtitle: item.fields.description ? (
            <RenderContentfulRichText
              document={item.fields.description}
              disableParagraphs
            />
          ) : undefined,
          speakers,
        };

        const existingEvents = eventsByDate.get(date) || [];
        eventsByDate.set(date, [...existingEvents, agendaItem]);
      });

      const schedule: Array<EventScheduleItemProps> = Array.from(
        eventsByDate.entries(),
      )
        .map(([date, agenda]) => ({
          date,
          agenda,
        }))
        .sort((a, b) => a.date.localeCompare(b.date));

      return (
        <EventSchedule
          schedule={schedule}
          alignment={entry.fields.alignment || "wide"}
        />
      );
    }

    if (ids.length > 0) {
      const sortedHits = data?.hits.toSorted((a, b) => {
        return ids.indexOf(a.id) - ids.indexOf(b.id);
      });

      if (!sortedHits || sortedHits.length === 0) {
        return null;
      }

      const eventsByDate = new Map<string, Array<EventAgendaItemProps>>();

      sortedHits.forEach((hit) => {
        const display = hit.display as EventSearchDisplay;

        // Extract date from sortDateS
        const date = hit.sortDate?.split("T")[0];

        if (!date) {
          return;
        }

        // Find time in eventMeta if it exists
        const timeInfo: EventMetaItem | undefined = display.eventMeta?.find(
          (group: EventMetaGroup) =>
            group.some((item: EventMetaItem) => item.title === "Times"),
        )?.[0];

        const timeValue = timeInfo?.info || "";

        // Split time range into start and end times
        const [startTime, endTime] = timeValue.split(" - ");

        // Find speakers in eventMeta if it exists
        const speakers: EventMetaItem | undefined = display.eventMeta
          ?.flatMap((group) => group) // Flatten the nested array
          .find((item: EventMetaItem) => item.title === "Participants");

        const speakersValue = speakers?.info;
        const speakersArray = speakersValue
          ? speakersValue.split(", ")
          : undefined;

        // Find location in eventMeta if it exists
        const locationInfo: EventMetaItem | undefined = display.eventMeta
          ?.flatMap((group) => group) // Flatten the nested array
          .find((item: EventMetaItem) => item.title === "Location");

        const locationValue = locationInfo?.info;

        const agendaItem: EventAgendaItemProps = {
          title: hit.title,
          times: endTime ? [startTime!, endTime] : [startTime!],
          location: locationValue ? { label: locationValue } : undefined,
          subtitle: hit.description ? hit.description : undefined,
          speakers: speakersArray
            ? speakersArray.map((speaker) => ({
                name: speaker,
              }))
            : undefined,
        };

        const existingEvents = eventsByDate.get(date) || [];
        eventsByDate.set(date, [...existingEvents, agendaItem]);
      });

      const schedule: Array<EventScheduleItemProps> = Array.from(
        eventsByDate.entries(),
      )
        .map(([date, agenda]) => ({
          date,
          agenda,
        }))
        .sort((a, b) => a.date.localeCompare(b.date));

      const alignment = entry.fields.alignment || "wide";

      return <EventSchedule schedule={schedule} alignment={alignment} />;
    }
  });
